// Página aberta: cabeçalho (ícone e título), propriedades das linhas,
// editor ou banco de dados, comentários. Também a barra superior (voltar,
// caminho, compartilhar) e o painel lateral ("peek") das linhas.

import * as store from "./store.js";
import * as router from "./router.js";
import * as P from "./props.js";
import { createEditor } from "./editor.js";
import { mountDatabase } from "./database.js";
import { el, icon, titleOf, debounce, toast, menu, relTime, fmtDateTime, confirmDialog } from "./dom.js";
import { shareDialog, moveDialog } from "./dialogs.js";
import { removePage, toggleCollapsed, pageMenu } from "./sidebar.js";
import { iconNode, ui } from "./icons.js";
import { iconPicker, coverPicker, coverCss, randomCover } from "./picker.js";
import { api } from "./api.js";
import { avatar } from "./people.js";
import { openLive, liveEnabled } from "./live.js";

const canEditNode = (n) => !n || store.canEdit(n);

// Monta uma página em `host`. opts: {peek: bool}
export function mountPage(host, id, opts = {}) {
  let data = store.cached(id);
  let editor = null;
  let db = null;
  let propsBox = null;
  let destroyed = false;
  let saveChain = Promise.resolve();
  let live = null; // sessão de edição simultânea (quando o servidor tem)
  let previewing = false; // editor só de leitura enquanto o documento sincroniza
  let presenceKey = "";
  const offs = [];
  const nodeInfo = () => store.node(id) || data?.page;
  const canEditRaw = () => (data ? data.role !== "view" : canEditNode(store.node(id)));
  const locked = () => !!data?.page.format?.locked;
  const canEdit = () => canEditRaw() && !locked();
  const kind = () => data?.page.kind || store.node(id)?.kind || "page";

  const page = el("article", { class: "page" + (opts.peek ? " in-peek" : "") });
  host.replaceChildren(page);

  // ---------- Cabeçalho ----------

  const iconBtn = el("button", { class: "page-icon", title: "Trocar ícone" });
  const addIcon = el("button", { class: "link add-icon" }, ui("page", 14), " Adicionar ícone");
  const addCover = el("button", { class: "link add-icon" }, ui("image", 14), " Adicionar capa");
  const addComment = el("button", { class: "link add-icon" }, ui("comment", 14), " Comentar");
  const cover = el("div", { class: "page-cover", hidden: true });
  const coverBtns = el("div", { class: "cover-actions" },
    el("button", { class: "small", onclick: (e) => coverPicker(e.currentTarget, setCover) }, "Trocar capa"),
    el("button", { class: "small", onclick: () => setCover(null) }, "Remover"));
  cover.append(coverBtns);
  const title = el("textarea", { class: "page-title", rows: 1, placeholder: "Sem título", spellcheck: "false" });

  const growTitle = () => { title.style.height = "auto"; title.style.height = title.scrollHeight + "px"; };
  const drawIcon = () => {
    const info = nodeInfo();
    iconBtn.replaceChildren(info?.icon ? iconNode(info.icon) : "");
    iconBtn.hidden = !info?.icon;
    addIcon.hidden = !!info?.icon || !canEdit();
    addCover.hidden = !!data?.page.cover || !canEdit() || kind() === "row";
    addComment.hidden = !data || kind() === "database";
    iconBtn.disabled = !canEdit();
    const bg = coverCss(data?.page.cover);
    cover.hidden = !bg;
    if (bg) cover.style.background = bg;
    coverBtns.hidden = !canEdit();
    page.classList.toggle("has-cover", !!bg);
  };
  const pickIcon = (anchor) => iconPicker(anchor, (v) => {
    setLocal({ icon: v });
    drawIcon();
    store.patch(id, { icon: v }).catch(() => {});
  }, { current: nodeInfo()?.icon });
  function setCover(v) {
    if (data) data.page.cover = v;
    drawIcon();
    store.patch(id, { cover: v }).catch(() => {});
  }
  iconBtn.addEventListener("click", () => canEdit() && pickIcon(iconBtn));
  addIcon.addEventListener("click", () => pickIcon(addIcon));
  addCover.addEventListener("click", () => setCover(randomCover()));
  addComment.addEventListener("click", () => {
    commentsHost.querySelector("textarea")?.scrollIntoView({ block: "center" });
    commentsHost.querySelector("textarea")?.focus();
  });

  const saveTitle = debounce((v) => store.patch(id, { title: v }).catch(() => {}), 450);
  title.addEventListener("input", () => {
    growTitle();
    const v = title.value.replace(/\n/g, " ");
    setLocal({ title: v });
    saveTitle(v);
  });
  title.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || (e.key === "ArrowDown" && title.selectionStart === title.value.length)) {
      e.preventDefault();
      saveTitle.flush();
      focusEditorStart();
    }
  });
  title.addEventListener("blur", () => saveTitle.flush());

  // Foco já (sem esperar o próximo quadro), para a próxima tecla cair no texto.
  function focusEditorStart() {
    if (!editor) return;
    editor.view.focus();
    editor.commands.setTextSelection(1);
  }

  // Atualiza o título/ícone na árvore e no banco em cache, sem esperar o servidor.
  function setLocal(changes) {
    const n = store.node(id);
    if (n) {
      Object.assign(n, changes);
      store.emit("tree");
    }
    if (data) Object.assign(data.page, changes);
    if (kind() === "row" && data?.page.parent_id) store.setRowLocal(data.page.parent_id, id, changes);
  }

  const header = el("header", { class: "page-header" }, el("div", { class: "page-icon-row" }, iconBtn, el("div", { class: "page-add-row" }, addIcon, addCover, addComment)), title);
  const propsHost = el("div", { class: "page-props" });
  const bodyHost = el("div", { class: "page-body" });
  const childHost = el("div", { class: "page-children" });
  const commentsHost = el("div", { class: "page-comments" });
  page.append(cover, header, propsHost, bodyHost, childHost, commentsHost);

  function drawHeader() {
    const info = nodeInfo();
    if (document.activeElement !== title) title.value = info?.title || "";
    title.readOnly = !canEdit();
    drawIcon();
    requestAnimationFrame(growTitle);
    page.classList.toggle("is-database", kind() === "database");
    const f = data?.page.format || {};
    page.classList.toggle("full-width", !!f.fullWidth || kind() === "database");
    page.classList.toggle("small-text", !!f.smallText);
    page.classList.toggle("font-serif", f.font === "serif");
    page.classList.toggle("font-mono", f.font === "mono");
    page.classList.toggle("locked", locked());
    if (editor && !previewing && editor.isEditable !== canEdit()) editor.setEditable(canEdit());
  }
  drawHeader();

  // ---------- Corpo ----------

  function mountBody() {
    if (destroyed || !data) return;
    page.classList.toggle("is-database", kind() === "database");
    if (kind() === "database") {
      if (!db) {
        bodyHost.replaceChildren();
        db = mountDatabase(bodyHost, id, {
          canEdit: canEdit(),
          openRow: (rowId, o = {}) => (o.full || opts.peek ? router.openPage(rowId) : openPeek(rowId)),
          openSettings: (tab, propId) => import("./dbsettings.js").then((m) => m.openDbSettings(id, tab, propId)),
        });
      }
      return;
    }
    if (kind() === "row") drawProps();
    if (!editor) {
      bodyHost.replaceChildren();
      const host = el("div", { class: "editor" });
      bodyHost.append(host);
      const placeholder = kind() === "row" ? "Escreva os detalhes ou digite \"/\" para inserir um bloco" : undefined;
      const ctx = {
        pageId: id,
        pageTitle: () => title.value,
        openPage: (pid) => router.openPage(pid),
        createChild: (k) => store.create({ parent_id: id, kind: k, title: k === "database" ? "Novo banco de dados" : "" }).id,
        flush: () => editor?.flush(),
      };
      if (liveEnabled() && store.state.me) startLive(host, ctx, placeholder);
      else {
        editor = createEditor(host, { content: data.page.content, editable: canEdit(), placeholder, onChange: (json) => saveContent(json), ctx });
        linkLater();
      }
    }
    drawComments();
  }

  // Edição simultânea: mostra o texto salvo (só leitura) enquanto o documento
  // vem do servidor ou do computador, e troca pelo editor ao vivo em seguida.
  function startLive(host, ctx, placeholder) {
    live = openLive(id);
    previewing = true;
    editor = createEditor(host, { content: data.page.content, editable: false, placeholder, ctx });
    const session = live;
    offs.push(session.onChange(() => {
      const people = session.people().filter((p) => p.id !== store.state.me?.id);
      const key = (session.status.connected ? "1" : "0") + (session.isReady ? "r" : "") + people.map((p) => p.id).join(",");
      if (key === presenceKey) return;
      presenceKey = key;
      store.emit("presence", id);
    }));
    session.ready.then(() => {
      if (destroyed || live !== session) return;
      const focused = editor?.isFocused;
      editor?.destroy();
      host.replaceChildren();
      previewing = false;
      editor = createEditor(host, { live: session, editable: canEdit(), placeholder, ctx });
      session.provider.awareness.setLocalStateField("editor", canEdit());
      if (focused) editor.commands.focus();
      store.emit("presence", id);
      linkLater();
    });
  }

  function saveContent(json) {
    if (data) data.page.content = json;
    saveChain = saveChain.then(() => store.patch(id, { content: json }).catch(() => {}));
    return saveChain;
  }

  const linkLater = debounce(() => { if (!destroyed) linkChildren(); }, 400);

  // Subpáginas que ainda não aparecem no texto ganham um bloco de link no fim,
  // como no Notion (criadas pela barra lateral, por exemplo).
  function linkChildren() {
    if (!editor || kind() !== "page" || previewing) return;
    // Com várias pessoas na página, só um app coloca os links (senão duplicam).
    if (live && !live.leader()) return;
    const kids = store.children(id);
    const linked = new Set();
    editor.state.doc.descendants((n) => { if (n.type.name === "pageLink" || n.type.name === "databaseView") linked.add(n.attrs.id); });
    const missing = kids.filter((k) => !linked.has(k.id));
    if (!missing.length) return;
    if (canEdit()) {
      const end = editor.state.doc.content.size;
      editor.chain().insertContentAt(end, missing.map((k) => ({ type: "pageLink", attrs: { id: k.id } }))).run();
      childHost.replaceChildren();
    } else {
      childHost.replaceChildren(el("div", { class: "child-list" }, missing.map((k) => el("button", { class: "page-link", onclick: () => router.openPage(k.id) },
        el("span", { class: "page-link-icon" }, icon(k)), el("span", { class: "page-link-title" }, titleOf(k))))));
    }
  }

  function drawProps() {
    const database = data.database || store.cached(data.page.parent_id)?.page;
    if (!database) return;
    const ctx = {
      users: data.users, canEdit: canEdit(), canEditSchema: canEdit(),
      openRow: (rowId) => (opts.peek ? swapPeek(rowId) : router.openPage(rowId)),
      setProp: (row, p, v) => {
        const changes = { props: { [p.id]: v } };
        store.setRowLocal(database.id, id, changes);
        data.page.props = { ...data.page.props, [p.id]: v };
        store.patch(id, changes).catch(() => {});
      },
      saveSchema: () => store.patch(database.id, { schema: database.schema }).catch(() => {}),
      openSettings: () => import("./dbsettings.js").then((m) => m.openDbSettings(database.id, "props")),
    };
    const box = P.panel(data.page, database, ctx);
    propsHost.replaceChildren(
      el("div", { class: "row-db" }, el("button", { class: "link", onclick: () => router.openPage(database.id) }, `${database.icon || "▦"} ${titleOf(database)}`)),
      box);
    propsBox = box;
  }

  let showResolved = false;
  let showHistory = false;
  function drawComments() {
    if (!data || kind() === "database") return;
    const all = data.activity || [];
    const comments = all.filter((a) => a.kind === "comment").sort((a, b) => a.at - b.at);
    const open = comments.filter((c) => !c.resolved_at);
    const resolved = comments.filter((c) => c.resolved_at);
    const history = all.filter((a) => a.kind !== "comment");
    const input = el("textarea", { rows: 1, placeholder: "Adicione um comentário… (@ para mencionar alguém)" });
    const mentions = new Map();
    const setActivity = (act) => { data.activity = act; drawComments(); };
    const send = async () => {
      const text = input.value.trim();
      if (!text) return;
      input.value = "";
      try {
        const ids = [...mentions].filter(([name]) => text.includes("@" + name)).map(([, uid]) => uid);
        setActivity((await api("POST", `/pages/${id}/comments`, { text, mentions: ids })).activity);
      } catch (err) {
        toast(err.message);
        input.value = text;
      }
    };
    input.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } });
    input.addEventListener("input", () => {
      input.style.height = "auto";
      input.style.height = input.scrollHeight + "px";
      const m = /@([\p{L}]*)$/u.exec(input.value.slice(0, input.selectionStart));
      if (!m) return;
      const q = m[1].toLowerCase();
      const users = (store.state.users || []).filter((u) => u.id !== store.state.me.id && u.name.toLowerCase().includes(q)).slice(0, 6);
      if (!users.length) return;
      menu(input, users.map((u) => ({ label: u.name, icon: "@", run: () => {
        const at = input.selectionStart - m[0].length;
        input.value = input.value.slice(0, at) + "@" + u.name + " " + input.value.slice(input.selectionStart);
        mentions.set(u.name, u.id);
        input.focus();
      } })), { align: "left" });
      requestAnimationFrame(() => input.focus());
    });
    const comment = (c) => el("div", { class: "comment" + (c.resolved_at ? " resolved" : "") },
      avatar({ id: c.user_id, name: c.user_name }, "sm"),
      el("div", { class: "comment-body" },
        el("div", { class: "comment-head" }, el("strong", {}, c.user_name || "Alguém"),
          el("span", { class: "muted small", title: fmtDateTime(c.at) }, relTime(c.at)),
          el("span", { class: "grow" }),
          el("button", { class: "icon-btn tiny", title: c.resolved_at ? "Reabrir" : "Resolver", onclick: async () => {
            setActivity((await api("POST", `/pages/${id}/comments/${c.id}/resolve`, { resolved: !c.resolved_at })).activity);
          } }, c.resolved_at ? "↺" : ui("check", 14)),
          c.user_id === store.state.me.id || data.role === "owner" ? el("button", { class: "icon-btn tiny", title: "Excluir", onclick: async () => {
            if (!(await confirmDialog("Excluir este comentário?", { ok: "Excluir", danger: true }))) return;
            setActivity((await api("DELETE", `/pages/${id}/comments/${c.id}`)).activity);
          } }, ui("trash", 14)) : null),
        el("div", { class: "comment-text" }, c.text)));
    commentsHost.replaceChildren(el("section", { class: "comments" },
      el("div", { class: "comments-head" }, el("h4", {}, "Comentários", open.length ? el("span", { class: "count" }, String(open.length)) : null),
        resolved.length ? el("button", { class: "link small", onclick: () => { showResolved = !showResolved; drawComments(); } },
          showResolved ? "Esconder resolvidos" : `Ver ${resolved.length} resolvido(s)`) : null,
        history.length ? el("button", { class: "link small", onclick: () => { showHistory = !showHistory; drawComments(); } },
          showHistory ? "Esconder histórico" : "Histórico de alterações") : null),
      open.map(comment),
      showResolved ? resolved.map(comment) : null,
      el("div", { class: "comment-new" }, avatar(store.state.me, "sm"), input,
        el("button", { class: "small", onclick: send }, "Enviar")),
      showHistory ? el("div", { class: "history" }, history.map((a) => el("div", { class: "act " + a.kind },
        el("span", { class: "act-who" }, a.user_name || "Ordo"),
        el("span", { class: "act-text" }, a.text),
        el("span", { class: "act-when", title: fmtDateTime(a.at) }, relTime(a.at))))) : null));
  }

  // ---------- Dados ----------

  async function load(fresh) {
    if (!data) bodyHost.replaceChildren(skeleton());
    try {
      const d = await store.load(id, { fresh });
      if (destroyed) return;
      const first = !data || !editor;
      const contentChanged = data && editor && JSON.stringify(d.page.content) !== JSON.stringify(editor.getJSON());
      data = d;
      drawHeader();
      if (first) mountBody();
      else {
        if (kind() === "row") drawProps();
        drawComments();
        // Na edição simultânea o texto chega pelo WebSocket, não por aqui.
        if (!live && contentChanged && !editor.isFocused && !editor.pending() && d.page.updated_by !== store.state.me.id) {
          editor.commands.setContent(d.page.content || "", { emitUpdate: false });
        }
      }
      if (opts.onLoad) opts.onLoad(d);
    } catch (err) {
      if (destroyed) return;
      if (err.status === 404) {
        bodyHost.replaceChildren(el("div", { class: "not-found" }, el("p", {}, "Esta página não existe mais ou você não tem acesso."),
          el("button", { onclick: () => router.go({ view: "home" }, { replace: true }) }, "Ir para o início")));
        title.value = "";
      } else if (!data) {
        bodyHost.replaceChildren(el("p", { class: "error" }, err.message, " ", el("button", { class: "link", onclick: () => load(true) }, "Tentar de novo")));
      }
    }
  }

  // Mostra o que já existe em cache na hora; confere com o servidor em seguida.
  if (data) {
    mountBody();
    load(true);
  } else load(false);

  offs.push(store.on("tree", () => {
    if (document.activeElement !== title) drawHeader();
    if (editor && kind() === "page") linkLater();
  }));
  offs.push(store.on("changes", ({ pages }) => {
    const mine = pages.find((p) => p.id === id);
    if (!mine) return;
    if (mine.archived_at) return;
    if (kind() === "row" && data) {
      data.page.props = mine.props || data.page.props;
      if (document.activeElement !== title) data.page.title = mine.title;
      drawHeader();
      drawProps();
    }
    if (mine.updated_by !== store.state.me.id) load(true);
  }));
  offs.push(store.on("page", (p) => {
    if (p.id !== id || !data) return;
    if (kind() === "row") drawProps();
  }));
  // Linhas de outro banco carregaram: as relações desta linha ganham os títulos.
  offs.push(store.on("rows", (dbId) => {
    if (kind() !== "row" || !data || dbId === data.page.parent_id) return;
    const schema = (data.database || store.cached(data.page.parent_id)?.page)?.schema;
    if (schema?.properties?.some((p) => p.type === "relation" && p.database === dbId)) drawProps();
  }));
  offs.push(store.on("removed", (rid) => {
    if (rid === id && opts.peek) opts.onClose?.();
  }));

  return {
    id,
    setFormat: (patch) => {
      if (!data) return;
      data.page.format = { ...(data.page.format || {}), ...patch };
      drawHeader();
      store.emit("tree");
      store.patch(id, { format: data.page.format }).catch(() => {});
    },
    setCover: (v) => setCover(v),
    pickIcon: () => pickIcon(iconBtn.hidden ? addIcon : iconBtn),
    reload: () => load(true),
    focusTitle: () => { title.focus(); title.select(); },
    flush: async () => {
      saveTitle.flush();
      await editor?.flush();
      await saveChain;
    },
    destroy: () => {
      destroyed = true;
      saveTitle.flush();
      editor?.flush();
      editor?.destroy();
      db?.destroy();
      for (const off of offs) off();
      live?.destroy();
      live = null;
    },
    // Quem mais está na página e como está a conexão (para a barra superior).
    presence: () => live ? { people: live.people().filter((p) => p.id !== store.state.me?.id), connected: live.status.connected, ready: live.isReady } : null,
    get data() { return data; },
  };
}

function skeleton() {
  return el("div", { class: "skeleton" }, [80, 95, 60, 88, 40].map((w) => el("div", { class: "sk-line", style: { width: w + "%" } })));
}

// ---------- Painel lateral (peek) ----------

let peek = null; // {id, view, box}

export function openPeek(rowId) {
  if (peek?.id === rowId) return;
  closePeek(true);
  const box = el("aside", { class: "peek" });
  const bodyEl = el("div", { class: "peek-body" });
  const bar = el("div", { class: "peek-bar" },
    el("button", { class: "icon-btn", title: "Fechar (Esc)", onclick: () => closePeek() }, "»"),
    el("button", { class: "icon-btn", title: "Abrir em página inteira", onclick: () => { const id = peek.id; closePeek(); router.openPage(id); } }, "⤢"),
    el("span", { class: "grow" }),
    el("button", { class: "icon-btn", title: "Mais", onclick: (e) => {
      const row = peek.view.data?.page;
      menu(e.currentTarget, [
        { label: "Abrir em página inteira", icon: "⤢", run: () => { const id = peek.id; closePeek(); router.openPage(id); } },
        { label: "Excluir", icon: "🗑", danger: true, run: () => { const id = peek.id; closePeek(); store.archive(id).catch(() => {}); toast("Movida para a lixeira."); } },
      ].filter(() => row), { align: "right" });
    } }, "⋯"));
  box.append(bar, bodyEl);
  document.getElementById("main").append(box);
  requestAnimationFrame(() => box.classList.add("open"));
  peek = { id: rowId, box, view: mountPage(bodyEl, rowId, { peek: true, onClose: () => closePeek() }) };
  peek.body = bodyEl;
  const created = store.cached(rowId);
  if (created && !created.page.title && created.page.created_at > Date.now() - 3000) requestAnimationFrame(() => peek?.view.focusTitle());
}

function swapPeek(rowId) {
  openPeek(rowId);
}

export function closePeek(instant = false) {
  if (!peek) return;
  const p = peek;
  peek = null;
  p.view.flush();
  p.view.destroy();
  if (instant) p.box.remove();
  else {
    p.box.classList.remove("open");
    setTimeout(() => p.box.remove(), 180);
  }
}

export const peekOpen = () => !!peek;

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && peek && !document.querySelector(".popup,dialog[open],.slash-menu")) closePeek();
});
document.addEventListener("mousedown", (e) => {
  if (!peek || peek.box.contains(e.target) || e.target.closest(".popup,dialog,.slash-menu,.format-bar")) return;
  // Clicar em outro card troca o peek; clicar fora fecha.
  if (e.target.closest(".card,.trow,.dlist-row,.cal-item")) return;
  closePeek();
});

// ---------- Barra superior ----------

const VIEW_LABEL = { settings: ["settings", "Configurações"], inbox: ["inbox", "Caixa de entrada"], tasks: ["tasks", "Minhas tarefas"],
  ai: ["ai", "Perguntar ao Ordo"], admin: ["admin", "Administração"], team: ["org", "Equipe"], home: ["home", "Início"] };

// "Compartilhar" com as fotos de quem recebeu a página, como no Notion.
function shareButton(id, view) {
  const shares = store.cached(id)?.shares || (view?.id === id ? view.data?.shares : null) || [];
  const pub = store.cached(id)?.publication || (view?.id === id ? view.data?.publication : null);
  return el("button", { class: "small share-btn", title: pub ? "Publicada na internet: qualquer pessoa com o link lê esta página" : "Compartilhar ou copiar o link", onclick: (e) => shareDialog(id, e.currentTarget) },
    pub ? el("span", { class: "published-dot" }, ui("globe", 14)) : null,
    shares.length ? el("span", { class: "avatar-stack" }, shares.slice(0, 3).map((u) => avatar(u, "sm"))) : ui("users", 14),
    " Compartilhar");
}

// Fotos de quem está com a página aberta agora, com a cor do cursor de cada um.
function presenceStack(view) {
  const people = view.presence?.()?.people || [];
  if (!people.length) return null;
  const names = people.map((p) => p.name).join(", ");
  return el("span", { class: "presence", title: people.length === 1 ? `${names} está nesta página` : `${names} estão nesta página` },
    people.slice(0, 4).map((p) => {
      const a = avatar(p, "sm");
      a.style.boxShadow = `0 0 0 2px ${p.color}`;
      return a;
    }),
    people.length > 4 ? el("span", { class: "presence-more" }, `+${people.length - 4}`) : null);
}

function liveState(view) {
  const live = view?.presence?.();
  if (!live) return "";
  if (!live.ready) return live.connected ? "Carregando…" : "Conectando…";
  if (!live.connected) return "Sem conexão: as edições serão enviadas quando voltar";
  return "";
}

export function renderTopbar(bar, route, savingCount, view) {
  const crumbs = el("nav", { class: "crumbs" });
  if (route.view === "page") {
    const info = store.node(route.id) || store.cached(route.id)?.page;
    const trail = [...store.ancestors(route.id), info].filter(Boolean);
    const ws = store.workspace((trail[0] || {}).workspace_id);
    if (ws) crumbs.append(el("span", { class: "crumb static ws-crumb" }, el("span", { class: "crumb-icon" }, iconNode(ws.icon, "🗂️")), el("span", { class: "crumb-title" }, ws.name)), el("span", { class: "crumb-sep" }, "/"));
    const shown = trail.length > 4 ? [trail[0], { ellipsis: true }, ...trail.slice(-2)] : trail;
    shown.forEach((c, i) => {
      if (i) crumbs.append(el("span", { class: "crumb-sep" }, "/"));
      if (c.ellipsis) crumbs.append(el("span", { class: "crumb muted" }, "…"));
      else crumbs.append(el("button", { class: "crumb", onclick: () => router.openPage(c.id) }, el("span", { class: "crumb-icon" }, icon(c)), el("span", { class: "crumb-title" }, titleOf(c))));
    });
  } else {
    const [ic, label] = VIEW_LABEL[route.view] || VIEW_LABEL.home;
    crumbs.append(el("span", { class: "crumb static" }, el("span", { class: "crumb-icon" }, ui(ic, 15)), " ", label));
  }
  const n = route.view === "page" ? (store.node(route.id) || store.cached(route.id)?.page) : null;
  const editable = n && canEditNode(store.node(route.id) || n);
  const fav = n && store.isFavorite(route.id);
  bar.replaceChildren(...[
    el("button", { class: "icon-btn menu-toggle", title: "Barra lateral (Ctrl+\\)", onclick: toggleCollapsed }, "☰"),
    el("button", { class: "icon-btn", title: "Voltar (Alt+←)", disabled: !router.canBack(), onclick: router.back }, "←"),
    el("button", { class: "icon-btn", title: "Avançar (Alt+→)", disabled: !router.canForward(), onclick: router.forward }, "→"),
    crumbs,
    el("span", { class: "grow" }),
    el("span", { class: "save-state" + (savingCount ? " busy" : "") }, savingCount ? "Salvando…" : liveState(view) || (n ? `Editado ${relTime(n.updated_at || Date.now())}` : "")),
    n && view?.id === route.id ? presenceStack(view) : null,
    n && view?.data?.page.format?.locked ? el("span", { class: "locked-badge", title: "Página travada: destrave no menu ⋯ para editar" }, ui("lock", 13), " Travada") : null,
    n ? shareButton(route.id, view) : null,
    n ? el("button", { class: "icon-btn" + (fav ? " on" : ""), title: fav ? "Remover dos favoritos" : "Adicionar aos favoritos", onclick: async () => {
      await store.toggleFavorite(route.id);
      renderTopbar(bar, route, savingCount, view);
    } }, ui(fav ? "star" : "star", 16)) : null,
    n ? el("button", { class: "icon-btn", title: "Mais opções", onclick: (e) => {
      const tn = store.node(route.id) || n;
      pageMenu(tn, e.currentTarget, view);
    } }, ui("more")) : null].filter(Boolean));
}
